import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { patchJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import type { Person, ProfileMembership } from '@/lib/contacts/types'
import { displayDate } from '@/lib/dates'

interface MembershipResponse {
  membership: ProfileMembership
}

interface PersonResponse {
  person: Person
}

type ApprovalPaymentHandling = 'none' | 'offline'

export function RenewMembershipDialog({
  person,
  membership,
  onClose,
  onRenewed,
}: {
  person: Person
  membership: ProfileMembership
  onClose: () => void
  onRenewed: () => Promise<void>
}) {
  const renewMembership = useMutation({
    mutationFn: () =>
      patchJSON<MembershipResponse>(`/api/v1/persons/${person.id}/membership/renew`, {
        membership: {
          payment_handling: 'offline',
          expected_renewal_due_on: membership.renewal_due_on,
        },
      }),
    onSuccess: onRenewed,
  })
  const fieldErrors = membershipFieldErrors(renewMembership.error)

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            renewMembership.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>Renew {membership.level}</DialogTitle>
            <DialogDescription>{person.name} · Active membership</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            {(fieldErrors.status || fieldErrors.paymentHandling || fieldErrors.membership) && (
              <Alert>
                {fieldErrors.status || fieldErrors.paymentHandling || fieldErrors.membership}
              </Alert>
            )}
            <PreviewRow
              label="Renewal due"
              value={`${displayDate(membership.renewal_due_on)} -> ${displayDate(
                membership.next_renewal_due_on,
              )}`}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={renewMembership.isPending}>
              {renewMembership.isPending ? 'Renewing...' : 'Renew membership'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function EndMembershipDialog({
  action,
  person,
  onClose,
  onChanged,
}: {
  action: 'lapse' | 'suspend'
  person: Person
  onClose: () => void
  onChanged: () => Promise<void>
}) {
  const [note, setNote] = React.useState('')
  const endMembership = useMutation({
    mutationFn: () =>
      patchJSON<MembershipResponse>(`/api/v1/persons/${person.id}/membership/${action}`, {
        membership: note.trim() ? { note } : {},
      }),
    onSuccess: onChanged,
  })
  const fieldErrors = membershipFieldErrors(endMembership.error)
  const isSuspend = action === 'suspend'
  const submitLabel = endMembershipSubmitLabel(isSuspend, endMembership.isPending)
  const error = fieldErrors.status || fieldErrors.membership

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            endMembership.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {isSuspend ? 'Suspend this membership?' : 'Lapse this membership?'}
            </DialogTitle>
            <DialogDescription>
              {endMembershipDescription(isSuspend, person.name)}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {error && <Alert>{error}</Alert>}
            <FormField
              label={isSuspend ? 'Reason' : 'Note (optional)'}
              htmlFor={`${action}-membership-note`}
            >
              <Textarea
                id={`${action}-membership-note`}
                rows={2}
                placeholder={
                  isSuspend
                    ? 'Recorded in the membership history.'
                    : 'Why is this membership lapsing?'
                }
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </FormField>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={endMembership.isPending}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function ApproveMembershipDialog({
  person,
  membership,
  onClose,
  onApproved,
}: {
  person: Person
  membership: ProfileMembership
  onClose: () => void
  onApproved: () => Promise<void>
}) {
  const [paymentHandling, setPaymentHandling] = React.useState<ApprovalPaymentHandling>('none')
  const approveMembership = useMutation({
    mutationFn: () =>
      patchJSON<MembershipResponse>(`/api/v1/persons/${person.id}/membership/approve`, {
        membership: approvalPayload(paymentHandling),
      }),
    onSuccess: onApproved,
  })
  const fieldErrors = membershipFieldErrors(approveMembership.error)
  const error = fieldErrors.status || fieldErrors.paymentHandling || fieldErrors.membership

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            approveMembership.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>Approve application</DialogTitle>
            <DialogDescription>
              {person.name} · {membership.level} · Pending-New → Active
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {error && <Alert>{error}</Alert>}
            <PreviewRow label="Current status" value={membership.status} />
            <FormField
              label="Payment handling"
              htmlFor="approve-membership-payment-handling"
              error={fieldErrors.paymentHandling}
            >
              <Select
                value={paymentHandling}
                onValueChange={(value) => setPaymentHandling(value as ApprovalPaymentHandling)}
              >
                <SelectTrigger id="approve-membership-payment-handling">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Approve without recording payment</SelectItem>
                  <SelectItem value="offline">Record offline payment</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={approveMembership.isPending}>
              {approveMembership.isPending ? 'Approving...' : 'Approve membership'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function approvalPayload(paymentHandling: ApprovalPaymentHandling) {
  return paymentHandling === 'offline' ? { payment_handling: 'offline' } : {}
}

export function RejectMembershipDialog({
  person,
  onClose,
  onRejected,
}: {
  person: Person
  onClose: () => void
  onRejected: () => Promise<void>
}) {
  const [note, setNote] = React.useState('')
  const rejectMembership = useMutation({
    mutationFn: () =>
      patchJSON<PersonResponse>(`/api/v1/persons/${person.id}/membership/reject`, {
        membership: note.trim() ? { note } : {},
      }),
    onSuccess: onRejected,
  })
  const fieldErrors = membershipFieldErrors(rejectMembership.error)
  const error = fieldErrors.status || fieldErrors.membership

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            rejectMembership.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>Reject application?</DialogTitle>
            <DialogDescription>
              {person.name} · Contact remains · Membership removed
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {error && <Alert>{error}</Alert>}
            <FormField label="Note (optional)" htmlFor="reject-membership-note">
              <Textarea
                id="reject-membership-note"
                rows={2}
                placeholder="Recorded in the membership history."
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </FormField>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={rejectMembership.isPending}>
              {rejectMembership.isPending ? 'Rejecting...' : 'Reject application'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function membershipFieldErrors(error: Error | null) {
  return {
    membershipLevel: apiFieldMessages(error, 'membership_level')[0],
    membership: apiFieldMessages(error, 'membership')[0],
    memberSince: apiFieldMessages(error, 'member_since_on')[0],
    paymentHandling: apiFieldMessages(error, 'payment_handling')[0],
    person: apiFieldMessages(error, 'person')[0],
    status: apiFieldMessages(error, 'status')[0],
  }
}

function endMembershipDescription(isSuspend: boolean, personName: string) {
  return `${personName} · Active → ${isSuspend ? 'Suspended' : 'Lapsed'}`
}

function endMembershipSubmitLabel(isSuspend: boolean, pending: boolean) {
  if (pending) return isSuspend ? 'Suspending...' : 'Lapsing...'

  return isSuspend ? 'Suspend membership' : 'Lapse membership'
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-body-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  )
}
